import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Car, Shield, Clock, Users, Award, Star, CheckCircle, Heart, Target, Globe, MapPin, Phone, Mail, ArrowRight, Plane, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useSiteConfig } from "@/hooks/useSiteConfig";

const About = () => {
  const { getConfig } = useSiteConfig();

  const values = [
    { icon: Shield, title: "Safety First", description: "Your safety is our top priority. All our vehicles are regularly inspected and maintained, and our drivers are fully licensed and insured." },
    { icon: Heart, title: "Customer Excellence", description: "We're dedicated to providing exceptional service that exceeds your expectations. Your satisfaction is our success." },
    { icon: Target, title: "Reliability", description: "Count on us for punctual service, transparent pricing, and consistent quality. We're committed to making your journey stress-free." },
    { icon: Globe, title: "Local Expertise", description: "As locals, we know Punta Cana and the Dominican Republic inside out. Let us share our knowledge and passion with you." }
  ];

  const team = [
    { name: "Professional Drivers", role: "Bilingual & Licensed", description: "Our drivers are carefully selected, professionally trained, and fluent in English and Spanish." },
    { name: "Customer Service Team", role: "24/7 Available", description: "Our dedicated support team is always ready to assist you before, during, and after your journey." },
    { name: "Operations Team", role: "Fleet Management", description: "Ensuring every vehicle is in top condition and every route is optimized for your comfort." }
  ];

  const whyChooseUs = [
    "Licensed and fully insured operations",
    "24/7 bilingual customer support",
    "Professional and experienced drivers",
    "Modern, clean, and comfortable fleet",
    "Transparent pricing with no hidden fees",
    "Instant booking confirmation",
    "Flexible cancellation policies",
    "Flight tracking for airport pickups"
  ];

  return (
    <Layout>
      <Helmet>
        <title>About Us | Smart Punta Cana Transfer - Best Airport Transportation</title>
        <meta name="description" content="Learn about Smart Punta Cana Transfer - your trusted partner for airport transfers in Punta Cana and throughout the Dominican Republic." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/95 to-accent" />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 right-20 w-72 h-72 bg-secondary/15 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <Badge className="mb-6 bg-secondary/90 text-secondary-foreground border-0 text-sm px-6 py-2 rounded-full">
              About Smart Punta Cana Transfer
            </Badge>
            <h1 className="text-5xl md:text-7xl font-black mb-6 leading-[0.9]">
              The <span className="text-secondary">Smart Way</span>
              <br />to Travel
            </h1>
            <p className="text-xl text-white/80 leading-relaxed max-w-2xl mx-auto font-light">
              Professional, reliable, and comfortable airport transfers in Punta Cana and throughout the Dominican Republic.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-background relative">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-secondary to-accent" />
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Star, number: "10+", label: "Years of Experience" },
              { icon: Users, number: "5,000+", label: "Happy Customers" },
              { icon: Award, number: "4.9/5", label: "Average Rating" },
              { icon: CheckCircle, number: "24/7", label: "Customer Support" }
            ].map((stat, index) => (
              <Card key={index} className="text-center border-0 shadow-md hover-lift p-6">
                <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-3xl font-black text-primary mb-1">{stat.number}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="mb-4 bg-secondary/10 text-secondary border-secondary/20 rounded-full px-6 py-2">Our Story</Badge>
              <h2 className="text-4xl font-bold mb-6">
                Connecting Travelers to <span className="text-primary">Caribbean Paradise</span>
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  {getConfig('about_story_paragraph_1', 'Smart Punta Cana Transfer was born from a passion for hospitality and a deep love for the Dominican Republic. Founded over a decade ago, we started with a simple mission: to provide travelers with safe, comfortable, and reliable transportation in Punta Cana.')}
                </p>
                <p>
                  {getConfig('about_story_paragraph_2', "What began as a small transfer service has grown into a comprehensive transportation solution, serving thousands of satisfied customers from around the world.")}
                </p>
                <p>
                  {getConfig('about_story_paragraph_3', "Today, we're proud to offer professional airport transfers that showcase the warmth and hospitality that make the Dominican Republic a world-class destination.")}
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-primary/20 to-accent/20">
                <img 
                  src="https://images.unsplash.com/photo-1506929562872-bb421503ef21?auto=format&fit=crop&q=80" 
                  alt="Punta Cana Beach"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-secondary to-tropical text-white p-5 rounded-2xl shadow-xl">
                <div className="flex items-center gap-3 mb-1">
                  <MapPin className="w-5 h-5" />
                  <span className="font-bold">Based in Punta Cana</span>
                </div>
                <p className="text-sm opacity-90">Serving all of Dominican Republic</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="border-0 shadow-md hover-lift overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-primary to-accent" />
              <CardHeader>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-4 shadow-lg">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-2xl">Our Mission</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed">
                  {getConfig('about_mission_text', "To provide safe, reliable, and comfortable transportation services that exceed our customers' expectations, while showcasing the beauty and hospitality of the Dominican Republic.")}
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md hover-lift overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-secondary to-tropical" />
              <CardHeader>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-secondary to-tropical flex items-center justify-center mb-4 shadow-lg">
                  <Globe className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-2xl">Our Vision</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed">
                  {getConfig('about_vision_text', 'To be the most trusted and preferred transfer service in the Dominican Republic, known for exceptional customer service, reliability, and creating seamless travel experiences.')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 rounded-full px-6 py-2">Our Values</Badge>
            <h2 className="text-4xl font-bold mb-4">What <span className="text-secondary">Drives Us</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="border-0 shadow-md hover-lift group">
                <CardHeader>
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <value.icon className="w-7 h-7 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{value.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-secondary/10 text-secondary border-secondary/20 rounded-full px-6 py-2">Our Team</Badge>
            <h2 className="text-4xl font-bold mb-4">Meet the People Behind <span className="text-primary">Your Journey</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {team.map((member, index) => (
              <Card key={index} className="text-center border-0 shadow-md hover-lift">
                <CardHeader>
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-accent mx-auto mb-4 flex items-center justify-center shadow-lg">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-lg">{member.name}</CardTitle>
                  <CardDescription className="text-secondary font-semibold">{member.role}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm leading-relaxed">{member.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Checklist */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-accent/10 text-accent border-accent/20 rounded-full px-6 py-2">Why Choose Us</Badge>
              <h2 className="text-4xl font-bold mb-4">The Smart Transfer <span className="text-secondary">Difference</span></h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {whyChooseUs.map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-4 rounded-xl hover:bg-background transition-colors">
                  <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0" />
                  <span className="text-muted-foreground">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-primary via-primary to-accent text-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-40 h-40 bg-secondary/20 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-60 h-60 bg-white/5 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <Zap className="h-12 w-12 mx-auto mb-6 text-secondary animate-float" />
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              Ready to Experience Smart Service?
            </h2>
            <p className="text-xl mb-8 text-white/80 font-light">
              Book your transfer today and discover why thousands of travelers trust us
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground text-lg px-8 rounded-full font-bold shadow-secondary">
                <Link to="/transfers">Book a Transfer</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10 text-lg px-8 rounded-full backdrop-blur-sm">
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>

            <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row gap-6 justify-center items-center text-white/70 text-sm">
              <a href={`tel:${getConfig('contact_phone', '+1 (809) 123-4567')}`} className="flex items-center gap-2 hover:text-white transition-colors">
                <Phone className="w-4 h-4" />
                <span>{getConfig('contact_phone', '+1 (809) 123-4567')}</span>
              </a>
              <a href={`mailto:${getConfig('contact_email', 'info@smartpuntacanatransfer.com')}`} className="flex items-center gap-2 hover:text-white transition-colors">
                <Mail className="w-4 h-4" />
                <span>{getConfig('contact_email', 'info@smartpuntacanatransfer.com')}</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
